<script>
    // 플레이 화면 미리보기 — **올린 그림이 게임에서 얼마나 크게 나오는지**를 보여준다.
    //
    // 왜 필요한가: 빌더 오른쪽 프리뷰 iframe 은 **메인 화면**(MainScene)을 띄운다. 그런데 이 탭의
    // 그림(배경·코인·적)은 전부 **플레이 화면**에서만 쓰인다 — 저작자가 확인하려면 게임을 시작해
    // 적이 지나가는 걸 눈으로 쫓아야 했다. 게다가 런타임이 원본 크기와 무관하게 정해진 크기로
    // 눕히므로(GAME sizing.ts), 1024px 짜리를 올리든 200px 짜리를 올리든 **썸네일로는 구분이 안 된다**.
    //
    // 움직이는 프리뷰 대신 **정지 화면**인 이유: 저작자가 알고 싶은 건 "얼마나 크나 / 배경 위에서
    // 보이나 / 여백이 얼마나 먹나"이고, 그건 멈춰 있어야 비교된다.
    import { LANES, ENEMY_KINDS, PLAY_SPRITE_SIZE } from "./gameSlots.js";

    /** 슬롯키 → 현재 버전 URL. 빈 슬롯은 "내장 기본" 자리로 그린다. */
    export let urls = {};

    // 런타임과 **같은 720px 논리 좌표계** 위에 그리고 통째로 축소한다.
    // 그래야 아래 숫자들이 전부 런타임 상수 그대로일 수 있다(px 를 따로 환산하지 않는다).
    const STAGE_W = 720;
    // 편대 한 줄 + 코인 줄이 들어갈 만큼만. 실제 화면(1560)을 다 그리면 탭이 세로로 늘어진다.
    const STAGE_H = 520;
    const LANE_W = STAGE_W / LANES;
    const laneX = (i) => LANE_W * (i + 0.5);

    const ENEMY = PLAY_SPRITE_SIZE.enemy;
    const COIN = PLAY_SPRITE_SIZE.coin;

    const ENEMY_ROW_Y = 140;
    const COIN_LANE = 4;
    const COIN_TOP_Y = 262;
    const COIN_GAP = 84; // 런타임 COIN.streamGap
    const COIN_COUNT = 3;

    // 배경은 런타임이 세로로 **반복**해 흘린다(tileSprite). 짧은 그림도 빈틈이 안 생기게 쌓아 둔다.
    const BG_TILES = [0, 1, 2];

    let boxW = STAGE_W;
    $: scale = Math.min(1, boxW ? boxW / STAGE_W : 1);
    $: offsetX = Math.max(0, (boxW - STAGE_W * scale) / 2);
</script>

<div class="frame" bind:clientWidth={boxW} style="height:{STAGE_H * scale}px">
    <div class="stage" style="left:{offsetX}px; transform:scale({scale})">
        {#if urls.background}
            <!-- ⚠️ crossorigin 필수. 이게 없으면 브라우저가 Origin 없는 응답을 캐시에 넣고,
                 같은 URL 을 XHR 로 받는 게임 텍스처가 그 캐시를 물어 CORS 로 막힌다. -->
            <div class="bg">
                {#each BG_TILES as t (t)}
                    <img crossorigin="anonymous" src={urls.background} alt="" />
                {/each}
            </div>
        {/if}

        <!-- 적이 내려오는 5개 레인. 편대 한 칸이 얼마나 넓은지가 크기 판단의 기준이 된다. -->
        {#each [1, 2, 3, 4] as n (n)}
            <div class="lane" style="left:{LANE_W * n}px"></div>
        {/each}

        <!-- 적 세 종류는 **같은 크기**로 나온다 — 구분은 저작자가 그린 색·실루엣이 한다. -->
        {#each ENEMY_KINDS as kind, i (kind.slot)}
            <div
                class="actor"
                style="left:{laneX(i)}px; top:{ENEMY_ROW_Y}px; width:{ENEMY.w}px; height:{ENEMY.h}px"
            >
                {#if urls[kind.slot]}
                    <img crossorigin="anonymous" src={urls[kind.slot]} alt="" />
                {:else}
                    <div class="placeholder" style="border-color:{kind.color}"></div>
                {/if}
                <span class="tag">{kind.label}</span>
            </div>
        {/each}

        <!-- 코인 줄 — 런타임도 한 레인에 세로로 흘려보낸다. -->
        {#each Array(COIN_COUNT) as _, i}
            <div
                class="actor"
                style="left:{laneX(COIN_LANE)}px; top:{COIN_TOP_Y + i * COIN_GAP}px; width:{COIN.w}px; height:{COIN.h}px"
            >
                {#if urls.coin}
                    <img crossorigin="anonymous" src={urls.coin} alt="" />
                {:else}
                    <div class="placeholder" style="border-color:#ffe66d"></div>
                {/if}
                {#if i === 0}<span class="tag">코인</span>{/if}
            </div>
        {/each}
    </div>
</div>

<p class="tw-mt-2 tw-text-[11px] tw-leading-relaxed tw-text-muted-foreground">
    실제 게임과 <strong class="tw-text-foreground">같은 크기</strong>입니다(화면 폭 720px 기준).
    올린 그림의 원본 해상도와 상관없이 이 크기로 나오므로, 그림 안의 투명 여백이 넓으면 그만큼 작아
    보입니다. 적 세 종류는 <strong class="tw-text-foreground">모두 같은 크기</strong>({ENEMY.w}px)로
    나오고 색·모양으로 구분됩니다. 점선은 아직 안 올린 자리 — 게임에선 내장 기본 그래픽이 나옵니다.
</p>

<style>
    /* 게임 화면이 어두워서 테마 토큰을 쓰지 않는다(빌더의 다른 게임 썸네일과 같은 바탕). */
    .frame {
        position: relative;
        width: 100%;
        overflow: hidden;
        border: 1px solid hsl(var(--sb-border));
        border-radius: 12px;
        background: #0d1430;
    }

    /* 720px 좌표계를 통째로 축소한다 — 자식은 런타임 숫자를 그대로 쓴다. */
    .stage {
        position: absolute;
        top: 0;
        left: 0;
        width: 720px;
        height: 520px;
        transform-origin: top left;
    }

    .bg {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    /* 런타임 fitBackgroundWidth 와 같다 — 가로를 화면 폭에 맞추고 세로는 비율대로 이어 붙인다. */
    .bg img {
        display: block;
        width: 720px;
        height: auto;
        flex: none;
    }

    .lane {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 1px;
        background: rgba(255, 255, 255, 0.07);
    }

    .actor {
        position: absolute;
        transform: translate(-50%, -50%);
    }

    .actor img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .placeholder {
        width: 100%;
        height: 100%;
        border: 2px dashed;
        border-radius: 8px;
        opacity: 0.55;
    }

    /* 크기 라벨. 축소 배율을 되돌려 항상 읽을 수 있는 크기로 둔다. */
    .tag {
        position: absolute;
        top: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
        font-size: 15px;
        color: rgba(255, 255, 255, 0.72);
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
    }
</style>
